import React, { useState, useMemo, useEffect } from "react";
import {
  X,
  Factory,
  User,
  Truck,
  Gem,
  CreditCard,
  MapPin,
  Edit3,
  AlertTriangle,
  ShieldCheck,
  PackageCheck,
  Layers,
  ListChecks,
  Siren,
  Save,
  Search, // <--- 1. Importei o ícone de busca
  Loader2,
  PenTool,
} from "lucide-react";
import EngravingDisplay from "../production/EngravingDisplay";
import { getEngravingBadgeStyle, parseEngravingParts, reassembleEngraving } from "../../utils/engravingUtils";


// --- CONFIGURAÇÕES ---
const STONE_COLORS = [
  "Vermelha",
  "Rosa",
  "Azul",
  "Azul Claro",
  "Verde",
  "Verde Escuro",
  "Amarela",
  "Roxa",
  "Laranja",
  "Peridoto",
  "Sky",
  "Branca",
  "Preta",
  "Marrom",
  "ESPECIAL",
  "SEM PEDRA",
];

const PE_TO_PRODUCTION_MAP = {
  pe_solicitado: "SOLICITACAO",
  pe_imprimindo: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_cura: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_fundicao: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_fundidos: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_acabamento: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_cravacao: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_polimento: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_finalizacao: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_conferencia: "INTERCEPTADO_FABRICA_ESTOQUE",
  pe_interceptado: "SOLICITACAO",
};

const PE_STATUS_LABELS = {
  pe_solicitado: "SOLICITADO",
  pe_imprimindo: "IMPRIMINDO",
  pe_cura: "CURA",
  pe_fundicao: "FUNDIÇÃO",
  pe_fundidos: "FUNDIDOS",
  pe_pre_escapulario: "PRÉ-ESCAPULÁRIO",
  pe_acabamento: "ACABAMENTO EXTERNO",
  pe_cravacao: "CRAVAÇÃO/GRAVAÇÃO",
  pe_polimento: "POLIMENTO",
  pe_revisao: "REVISÃO",
  pe_finalizacao: "RESINA/FINALIZAÇÃO",
  pe_conferencia: "CONFERÊNCIA",
  pe_interceptado: "INTERCEPTADO",
};

const MANUAL_STATUS_OPTIONS = [
  { value: "REVISAO", label: "REVISÃO" },
  { value: "SOLICITACAO", label: "AGUARDANDO ANÁLISE" },
  { value: "GRAVACAO", label: "GRAVAÇÃO" },
  { value: "FALHA_BANCA", label: "FALHA BANCA" },
  { value: "MANUTENCAO", label: "AJUSTE/MANUTENÇÃO" },
];

export default function ProductionConversionModal({
  isOpen,
  onClose,
  onConfirm,
  reservation,
  findCatalogItem,
  inventory = [],
  isEditing = false,
  isSaveOnlyMode = false, // <--- NOVO PROP PARA MODO EDIÇÃO
}) {
  const safeRes = reservation || {};
  const orderMetadata = safeRes.order_metadata || {};
  const orderDirect = safeRes.order || {};
  
  const safeOrder = { 
    ...orderDirect, 
    ...orderMetadata,
    customer: { ...(orderDirect.customer || {}), ...(orderMetadata.customer || {}) }
  };
  
  const safeCustomer = safeOrder.customer || {};
  const safePayment = safeOrder.payment || {};
  const safeShipping = safeRes.shipping || {};
  const safeAddress = safeShipping.address || {};
  const safeSpecs = safeRes.specs || {};

  const [submitting, setSubmitting] = useState(false);

  // --- STATE ---
  const [formData, setFormData] = useState({
    // --- 2. Adicionei o SKU ao estado ---
    sku: "",
    exception: "", // <--- Declarando exception para evitar warnings de input descontrolado

    specs: {
      size: "",
      stoneType: "",
      stoneColor: "",
      stoneBatch: "",
      finishing: "",
      engraving: "",
      standardColor: "",
      jewelryType: "",
      material: "",
      category: "",
    },
    order: {
      number: "",
      notes: "",
      customer: { name: "", cpf: "", email: "", phone: "" },
      payment: { method: "", total: "" },
    },
    shipping: {
      tipoenvio: "",
      price: "",
      tracking: "",
      address: {
        destinatario: "",
        street: "",
        number: "",
        complemento: "",
        city: "",
        statecode: "",
        zip: "",
        district: "",
      },
    },
    customDate: "",
    orderNumber: "",
    exception: "",
    useStock: false,
    initialStatus: "SOLICITACAO",
    isStatusLocked: false,
  });

  const [selectedStockItemId, setSelectedStockItemId] = useState(null);

  // Reset selected stock item when SKU changes
  useEffect(() => {
    setSelectedStockItemId(null);
  }, [formData.sku]);

  // Carrega status salvos para resolver labels customizados
  const resolvedStatuses = useMemo(() => {
    const saved = localStorage.getItem("pe_custom_statuses");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error("Erro parsing saved statuses in modal:", e);
      }
    }
    return [];
  }, []);

  const getStatusLabel = (statusId) => {
    if (!statusId) return "";
    if (PE_STATUS_LABELS[statusId]) {
      return PE_STATUS_LABELS[statusId];
    }
    const foundCustom = resolvedStatuses.find(s => s.id === statusId);
    if (foundCustom) {
      return foundCustom.label.toUpperCase();
    }
    return statusId.replace(/^pe_/, "").replace(/_/g, " ").toUpperCase();
  };

  // --- 1. LÓGICA DE ESTOQUE (Agora reativa ao formData.sku) ---
  
  // Todos os itens de estoque/PE válidos para este SKU
  const skuStockItems = useMemo(() => {
    if (!inventory || inventory.length === 0 || !formData.sku) return [];
    const targetSku = String(formData.sku).trim().toUpperCase();
    return inventory.filter(
      (i) =>
        String(i.sku).trim().toUpperCase() === targetSku &&
        (i.status === "in_stock" || (i.isPE && i.status !== "pe_interceptado" && i.status !== "adjusted_out" && i.status !== "sold"))
    );
  }, [inventory, formData.sku]);

  // Itens em PE apenas
  const peCandidates = useMemo(() => {
    return skuStockItems.filter(i => i.isPE);
  }, [skuStockItems]);

  // Estoque físico apenas
  const physicalStockItems = useMemo(() => {
    return skuStockItems.filter(i => !i.isPE && i.status === "in_stock");
  }, [skuStockItems]);

  // Item de estoque/PE ativo (ou auto-selecionado)
  const stockItem = useMemo(() => {
    if (isEditing) return null;
    
    // Se o usuário selecionou um ID explicitamente
    if (selectedStockItemId) {
      const found = skuStockItems.find(i => i.id === selectedStockItemId);
      if (found) return found;
    }
    
    // Fallback: Auto-seleciona
    // 1. Prioridade: Estoque físico
    if (physicalStockItems.length > 0) return physicalStockItems[0];
    
    // 2. Prioridade: PE candidates por prioridade de etapa
    if (peCandidates.length > 0) {
      const PRIORITY_MAP = {
        pe_conferencia: 8,
        pe_finalizacao: 7,
        pe_polimento: 6,
        pe_cravacao: 5,
        pe_acabamento: 4,
        pe_fundicao: 3,
        pe_imprimindo: 2,
        pe_solicitado: 1,
      };
      
      const sorted = [...peCandidates].sort((a, b) => {
        const scoreA = PRIORITY_MAP[a.status] || 0;
        const scoreB = PRIORITY_MAP[b.status] || 0;
        return scoreB - scoreA;
      });
      return sorted[0];
    }
    
    return null;
  }, [selectedStockItemId, skuStockItems, peCandidates, physicalStockItems, isEditing]);

  const hasStock = !!stockItem;
  const isPEItem = stockItem?.isPE || false;
  const hasPhysicalStock = physicalStockItems.length > 0;
  const hasPECandidates = peCandidates.length > 0;
  const anyStockAvailable = hasPhysicalStock || hasPECandidates;

  // --- 2. EXTRAÇÃO CATÁLOGO (Agora reativa ao formData.sku) ---
  const catalogData = useMemo(() => {
    // Verifica formData.sku
    if (!findCatalogItem || !formData.sku) return { found: false };

    const item = findCatalogItem(formData.sku);
    if (!item) return { found: false };

    const extracted = {
      found: true,
      standardColor: "MANUAL",
      jewelryType: "MANUAL",
      material: "MANUAL",
      category: "MANUAL",
      price: Number(item.price || 0),
    };

    if (item && item.tags) {
      try {
        let tagsArray =
          typeof item.tags === "string"
            ? JSON.parse(item.tags.replace(/\\"/g, '"'))
            : item.tags;

        if (Array.isArray(tagsArray)) {
          const findTag = (g) =>
            tagsArray.find((t) => t.group === g)?.value || "MANUAL";
          extracted.standardColor = findTag("Cor da Pedra");
          extracted.jewelryType = findTag("Tipo de Joia");
          extracted.material = findTag("Material");
          extracted.category = findTag("Categoria");
        }
      } catch (e) {
        console.warn("Erro ao parsear tags", e);
      }
    }
    return extracted;
  }, [formData.sku, findCatalogItem]); // Dependência atualizada

  // --- EFEITO LATERAL: Atualizar specs quando o Catálogo muda pelo SKU ---
  useEffect(() => {
    // Só roda se o catálogo trouxe dados novos válidos (item foi encontrado)
    if (catalogData.found) {
      // --- LÓGICA DE COR INTELIGENTE ---
      const catColorUpper = String(catalogData.standardColor || "").toUpperCase();
      let bestMatchColor = catalogData.standardColor || "MANUAL";

      // 1. Tenta achar a cor exata na lista do dropdown
      const exactMatch = STONE_COLORS.find(
        (c) => c.toUpperCase() === catColorUpper
      );

      if (exactMatch) {
        bestMatchColor = exactMatch;
      } else {
        // 2. Se não achar, tenta busca parcial (ex: "Azul" acha "Azul Claro")
        const partialMatch = STONE_COLORS.find(
          (c) =>
            c.toUpperCase().includes(catColorUpper) ||
            catColorUpper.includes(c.toUpperCase())
        );
        if (partialMatch) bestMatchColor = partialMatch;
      }
      // -----------------------------------------------------------

      setFormData((prev) => {
        // (FIX): Só sobrescreve a escolha do usuário se ele mudou o SKU.
        // Se estamos abrindo o modal com o mesmo SKU original, mantemos o que estava salvo (ex: Azul).
        const originalSku = reservation?.sku || "";
        const currentSku = prev.sku || "";
        const isSkuChanged = String(originalSku).trim().toUpperCase() !== String(currentSku).trim().toUpperCase();

        const currentChoice = prev.specs.stoneColor;
        const hasUserChoice = currentChoice && !["ND", "N/A", "", "SELECIONE..."].includes(currentChoice.toUpperCase());

        // Se o SKU não mudou e já temos uma escolha válida, preserva ela.
        // Caso contrário (SKU novo ou sem escolha), usa o padrão do catálogo.
        const finalColor = (!isSkuChanged && hasUserChoice) ? currentChoice : bestMatchColor;

        // Auto-preenche o preço se o campo estiver vazio ou for zero
        const currentTotal = String(prev.order?.payment?.total || "").trim();
        const parsedCurrentTotal = parseFloat(currentTotal.replace(",", "."));
        const shouldPrefillPrice = !currentTotal || isNaN(parsedCurrentTotal) || parsedCurrentTotal === 0;

        const finalTotal = shouldPrefillPrice && catalogData.price > 0
          ? String(catalogData.price)
          : prev.order?.payment?.total || "";

        return {
          ...prev,
          specs: {
            ...prev.specs,
            // Atualiza os campos de referência (Cinzas - Sempre Catalog)
            standardColor: catalogData.standardColor,
            jewelryType: catalogData.jewelryType,
            material: catalogData.material,
            category: catalogData.category,

            // ATUALIZA O DROPDOWN (Com proteção da escolha)
            stoneColor: finalColor,
          },
          order: {
            ...prev.order,
            payment: {
              ...prev.order?.payment,
              total: finalTotal,
            }
          }
        };
      });
    }
  }, [catalogData]);

  // --- INICIALIZAÇÃO ---
  useEffect(() => {
    if (isOpen && reservation) {
      let startStatus = "SOLICITACAO";
      let locked = false;
      let shouldUseStock = false;

      // Nota: A lógica de estoque inicial vai rodar, mas o useMemo acima
      // vai corrigir se o SKU mudar depois.
      if (hasStock && stockItem) {
        shouldUseStock = true;
        if (stockItem.isPE) {
          startStatus = "INTERCEPTADO_FABRICA_ESTOQUE";
          locked = true;
        } else {
          startStatus = "INTERCEPTADO_ESTOQUE";
        }
      }

      let fixedStoneType = safeSpecs.stoneType || "";
      if (fixedStoneType && fixedStoneType.toLowerCase().includes("zircônia"))
        fixedStoneType = "Zircônia";

      let targetColor = safeSpecs.stoneColor || "";
      const invalidValues = ["ND", "N/D", "SEM COR", "NAO DEFINIDO", ""];
      const isInvalidColor =
        !targetColor || invalidValues.includes(targetColor.toUpperCase());
      const hasCatalogColor =
        catalogData.standardColor && catalogData.standardColor !== "MANUAL";

      if (isInvalidColor && hasCatalogColor) {
        const catColorUpper = catalogData.standardColor.toUpperCase();
        const exactMatch = STONE_COLORS.find(
          (c) => c.toUpperCase() === catColorUpper
        );

        if (exactMatch) {
          targetColor = exactMatch; // Usa o formato bonito da lista
        } else {
          // Também corrige a busca parcial para ignorar maiúsculas/minúsculas
          const partialMatch = STONE_COLORS.find(
            (c) =>
              c.toUpperCase().includes(catColorUpper) ||
              catColorUpper.includes(c.toUpperCase())
          );
          targetColor = partialMatch || catalogData.standardColor;
        }
      }

      let defaultTotal = safePayment.total || "";
      if (!defaultTotal && findCatalogItem && safeRes.sku) {
        const catItem = findCatalogItem(safeRes.sku);
        if (catItem && catItem.price) {
          defaultTotal = String(catItem.price);
        }
      }

      setFormData({
        // --- 3. Inicializa SKU ---
        sku: safeRes.sku || "",

        useStock: shouldUseStock,
        initialStatus: startStatus,
        isStatusLocked: locked,
        customDate: (() => {
          const raw = safeRes.customCreatedAt || safeRes.created || safeRes.createdAt || safeRes.dateStr;
          if (!raw) return new Date().toISOString().split("T")[0];
          try {
            if (raw.toDate) return raw.toDate().toISOString().split("T")[0];
            const d = new Date(raw);
            if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
          } catch (e) { }
          return new Date().toISOString().split("T")[0];
        })(),
        orderNumber: safeOrder.number || safeRes.orderNumber || "",
        exception: safeRes.exception || safeSpecs.exception || "", // <--- Lê da raiz ou de dentro do specs JSON

        specs: {
          size: safeSpecs.size || "",
          stoneType: fixedStoneType,
          stoneColor: targetColor,
          standardColor:
            safeSpecs.standardColor || catalogData.standardColor || "",
          jewelryType: safeSpecs.jewelryType || catalogData.jewelryType || "",
          material: safeSpecs.material || catalogData.material || "",
          category: safeSpecs.category || catalogData.category || "",
          engraving: safeSpecs.engraving || "",
          finishing: safeSpecs.finishing || "",
          stoneBatch: safeSpecs.stoneBatch || "",
        },
        order: {
          number: safeOrder.number || "",
          notes: safeOrder.notes || safeRes.notes || safeRes.note || "",
          customer: {
            name: safeCustomer.name || safeRes.customerName || "",
            cpf: safeCustomer.cpf || "",
            email: safeCustomer.email || "",
            phone: safeCustomer.phone || "",
          },
          payment: {
            method: safePayment.method || "",
            total: defaultTotal,
          },
        },
        shipping: {
          tipoenvio: safeShipping.tipoenvio || safeShipping.method || "",
          price: safeShipping.price || "",
          tracking: safeShipping.rastreamento || safeShipping.tracking || "",
          address: {
            destinatario:
              safeAddress.destinatario ||
              safeAddress.recipient ||
              safeCustomer.name ||
              "",
            street: safeAddress.street || "",
            number: safeAddress.number || "",
            complemento: safeAddress.complemento || "",
            city: safeAddress.city || "",
            statecode: safeAddress.statecode || safeAddress.state || "",
            zip: safeAddress.zip || "",
            district: safeAddress.district || "",
          },
        },
      });
    }
  }, [isOpen, reservation]); // Removi dependencias de calculo para evitar reset ao digitar

  const handleStockToggle = (checked) => {
    let newStatus = "SOLICITACAO";
    let newLocked = false;
    if (checked && stockItem) {
      if (stockItem.isPE) {
        newStatus = "INTERCEPTADO_FABRICA_ESTOQUE";
        newLocked = true;
      } else {
        newStatus = "INTERCEPTADO_ESTOQUE";
      }
    }
    setFormData((prev) => ({
      ...prev,
      useStock: checked,
      initialStatus: newStatus,
      isStatusLocked: newLocked,
    }));
  };

  const update = (section, field, value, subField = null) => {
    setFormData((prev) => {
      const newData = { ...prev };
      if (section === "root") {
        newData[field] = value;
      } else {
        if (!newData[section]) newData[section] = {};
        if (subField) {
          if (!newData[section][field]) newData[section][field] = {};
          newData[section][field][subField] = value;
        } else {
          newData[section][field] = value;
        }
      }
      return newData;
    });
  };

  const handleUpdateEngravingPart = (partIdx, newPartText) => {
    const raw = formData.specs.engraving || "";
    const parts = parseEngravingParts(raw);
    if (parts[partIdx]) {
      parts[partIdx].text = newPartText;
      const reassembled = reassembleEngraving(parts);
      update("specs", "engraving", reassembled);
    }
  };

  const handleSubmit = async () => {
    // Validação de Valor Total obrigatório na conversão para pedido
    const isConversion = isEditing !== true && isSaveOnlyMode !== true;
    if (isConversion) {
      const valTotalStr = String(formData?.order?.payment?.total || "").trim();
      const parsedVal = parseFloat(valTotalStr.replace(",", "."));
      if (!valTotalStr || isNaN(parsedVal) || parsedVal <= 0) {
        alert("Por favor, preencha o campo 'Valor Total (R$)' com um valor válido maior que zero para converter em pedido.");
        return;
      }
    }

    if (submitting) return;
    setSubmitting(true);

    try {
      if (isEditing) {
        await onConfirm({
          id: safeRes.id,
          specs: { ...formData.specs, exception: formData.exception }, // Pack no JSON também
          exception: formData.exception,
          sku: formData.sku, // <--- 4. Envia o SKU editado
        });
        return;
      }

      let finalException = formData.exception || "";
      if (formData.useStock && stockItem && stockItem.isPE) {
        const pStatusLabel = getStatusLabel(stockItem.status);
        const interceptNotice = `[Interceptado de: ${pStatusLabel}]`;
        if (!finalException.includes("Interceptado de: ")) {
          finalException = finalException ? `${interceptNotice} ${finalException}` : interceptNotice;
        }
      }

      const finalData = {
        ...reservation,
        sku: formData.sku, 
        specs: { ...safeSpecs, ...formData.specs, exception: finalException }, // Empacota no JSON
        customCreatedAt: formData.customDate,
        exception: finalException, // Mantém na raiz
        order: {
          ...safeOrder,
          ...formData.order,
          number: formData.orderNumber,
          customer: { ...safeCustomer, ...formData.order.customer },
          payment: { ...safePayment, ...formData.order.payment },
        },
        shipping: {
          ...safeShipping,
          ...formData.shipping,
          method: formData.shipping.tipoenvio,
          tracking: formData.shipping.tracking,
          address: {
            ...safeAddress,
            ...formData.shipping.address,
            recipient: formData.shipping.address.destinatario,
            state: formData.shipping.address.statecode,
          },
        },
        status: formData.initialStatus, // (FIX: Usa o status escolhido no dropdown, seja manual ou auto)
        fromStock: formData.useStock,
        stockItemId: formData.useStock ? stockItem?.id : null,
        isInterceptedPE: formData.useStock ? stockItem?.isPE || false : false,
        interceptedFromStatus: formData.useStock && stockItem?.isPE ? stockItem.status : null,
      };

      await onConfirm(finalData);
    } catch (e) {
      console.error("Erro no envio:", e);
    } finally {
      setSubmitting(false);
    }
  };

  const showFullForm = !isEditing;
  const showColorWarning =
    formData.specs.stoneColor &&
    formData.specs.standardColor &&
    formData.specs.standardColor !== "MANUAL" &&
    formData.specs.stoneColor.trim().toUpperCase() !==
    formData.specs.standardColor.trim().toUpperCase();
  const isNatural = String(formData.specs.stoneType || "")
    .trim()
    .toLowerCase()
    .includes("natural");
  const StatusSelector = () => {
    const isPhysicalStock = formData.useStock && stockItem && !stockItem.isPE;
    const isPE = formData.useStock && stockItem && stockItem.isPE;

    let availableOptions = MANUAL_STATUS_OPTIONS;

    // 1. OPÇÕES PARA ESTOQUE FÍSICO
    if (isPhysicalStock) {
      availableOptions = [
        { value: "INTERCEPTADO_ESTOQUE", label: "INTERCEPTADO DO ESTOQUE" },
        { value: "REVISAO", label: "REVISÃO" },
        { value: "SOLICITACAO", label: "AGUARDANDO ANÁLISE" },
        { value: "GRAVACAO", label: "GRAVAÇÃO" },
        { value: "MANUTENCAO", label: "AJUSTE/MANUTENÇÃO" },
        { value: "PEDIDO_PRONTO", label: "✅ PEDIDO PRONTO" },
      ];
    }
    else if (isPE) {
      availableOptions = [
        { value: "INTERCEPTADO_FABRICA_ESTOQUE", label: "INTERCEPTADO DA FÁBRICA DE ESTOQUE" },
        { value: "REVISAO", label: "REVISÃO" },
        { value: "SOLICITACAO", label: "AGUARDANDO ANÁLISE" },
        { value: "GRAVACAO", label: "GRAVAÇÃO" },
        { value: "POLIMENTO", label: "POLIMENTO" },
        { value: "RESINA_FINALIZACAO", label: "RESINA/FINALIZAÇÃO" },
        { value: "ACABAMENTO_EXTERNO", label: "ACABAMENTO EXTERNO" },
      ];
    }

    return (
      <div className="flex items-center gap-2 bg-white/80 p-1.5 rounded-lg border border-slate-200 shadow-sm ml-auto">
        <label className="text-[10px] font-bold text-slate-500 uppercase flex items-center gap-1">
          <ListChecks size={12} /> Destino:
        </label>
        {formData.isStatusLocked ? (
          <span className="font-bold text-orange-700 text-xs flex items-center gap-1 bg-orange-100 px-2 py-1 rounded border border-orange-200">
            {availableOptions.find(
              (opt) => opt.value === formData.initialStatus
            )?.label || formData.initialStatus.replace(/_/g, " ")}
            <span className="text-[8px] bg-orange-200 px-1 rounded text-orange-800 opacity-70">
              AUTO
            </span>
          </span>
        ) : (
          <select
            className="font-bold text-slate-700 text-xs bg-transparent outline-none cursor-pointer min-w-[140px]"
            value={formData.initialStatus}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                initialStatus: e.target.value,
              }))
            }
          >
            {availableOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
      <div
        className={`bg-white w-full ${isEditing ? "max-w-2xl" : "max-w-5xl"
          } rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]`}
      >
        {/* HEADER */}
        <div className="bg-purple-600 p-4 flex justify-between items-center text-white shrink-0">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white/20 p-2 rounded-lg">
              {isEditing ? <Edit3 size={24} /> : <Factory size={24} />}
            </div>
            <div className="flex-1">
              <h2 className="font-bold text-lg leading-tight">
                {isEditing ? "Editar Especificações" : "Converter para Pedido"}
              </h2>

              {/* --- 5. INPUT DE SKU NO HEADER --- */}
              <div className="flex items-center gap-2 mt-1">
                <div className="relative group w-full max-w-[200px]">
                  <Search
                    size={12}
                    className="absolute left-2 top-1/2 -translate-y-1/2 text-purple-200 group-hover:text-white transition-colors"
                  />
                  <input
                    type="text"
                    className="bg-purple-700/50 border border-purple-500 rounded px-2 py-0.5 pl-6 text-xs font-mono text-white placeholder-purple-300 w-full focus:outline-none focus:bg-purple-700 focus:border-white transition-all uppercase font-bold"
                    value={formData.sku}
                    onChange={(e) =>
                      update("root", "sku", e.target.value.toUpperCase())
                    }
                    title="Editar SKU do Produto"
                  />
                </div>
                {isEditing && safeOrder.number && (
                  <span className="text-purple-200 text-xs font-mono">
                    | Pedido {safeOrder.number}
                  </span>
                )}
              </div>
              {/* ---------------------------------- */}
            </div>
          </div>
          <button
            onClick={submitting ? undefined : onClose}
            disabled={submitting}
            className={`hover:bg-white/20 p-2 rounded-full transition-colors ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <X size={20} />
          </button>
        </div>

        {/* CORPO */}
        <div className="p-6 overflow-y-auto space-y-6 custom-scrollbar flex-1">
           {/* BLOCO ESTOQUE */}
          {/* --- LÓGICA DE ESTOQUE (Só mostra se não estiver no modo Save Only) --- */}
          {!isSaveOnlyMode && (
            <>
              {anyStockAvailable && !isEditing && (
                <div className="border rounded-xl p-4 bg-slate-50 border-slate-200 flex flex-col gap-4 mb-4 animate-fade-in">
                  <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-full bg-purple-100 text-purple-600">
                        <Layers size={24} />
                      </div>
                      <div>
                        <h3 className="font-bold text-sm text-slate-800">
                          Itens Disponíveis em Estoque ou Produção (PE)
                        </h3>
                        <p className="text-xs font-medium text-slate-500">
                          Selecione um item abaixo para vincular a este pedido ou desmarque para criar um novo lote.
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 w-full md:w-auto bg-white p-3 rounded-lg border shadow-sm shrink-0">
                      <label className="flex items-center gap-2 cursor-pointer text-xs font-bold text-slate-700 select-none">
                        <input
                          type="checkbox"
                          className="w-5 h-5 rounded focus:ring-opacity-50 text-purple-600 focus:ring-purple-500"
                          checked={formData.useStock}
                          onChange={(e) => handleStockToggle(e.target.checked)}
                        />
                        <span className="uppercase">
                          Vincular Item (Estoque/PE)
                        </span>
                      </label>
                      <div className="pl-3 border-l border-slate-200 ml-2">
                        <StatusSelector />
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {/* Estoque Físico */}
                    {hasPhysicalStock && (
                      <div className="border-t border-slate-200/80 pt-3">
                        <h4 className="text-xs font-bold text-emerald-800 uppercase flex items-center gap-1.5 mb-2">
                          <PackageCheck size={14} className="text-emerald-600" /> Estoque Físico Pronto (Prateleira)
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {physicalStockItems.map((candidate) => {
                            const isSelected = stockItem?.id === candidate.id && formData.useStock;
                            return (
                              <div
                                key={candidate.id}
                                onClick={() => {
                                  setSelectedStockItemId(candidate.id);
                                  setFormData(prev => ({
                                    ...prev,
                                    useStock: true,
                                    initialStatus: "INTERCEPTADO_ESTOQUE",
                                    isStatusLocked: false
                                  }));
                                }}
                                className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all flex items-center justify-between ${
                                  isSelected
                                    ? "bg-emerald-600 text-white border-emerald-700 shadow-md ring-2 ring-emerald-400"
                                    : "bg-white hover:bg-emerald-50 text-slate-700 border-slate-200"
                                }`}
                              >
                                <div className="min-w-0 flex-1 pr-2">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${
                                      isSelected ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-800"
                                    }`}>
                                      PRONTO
                                    </span>
                                    <span className="font-mono text-[11px] font-bold truncate">
                                      ID: {candidate.id.substring(0, 8)}
                                    </span>
                                  </div>
                                  <div className={`text-[10px] font-medium mt-1 truncate ${
                                    isSelected ? "text-emerald-100" : "text-slate-500"
                                  }`}>
                                    Adicionado em: {(() => {
                                      const val = candidate.dateIn || candidate.created;
                                      if (!val) return "Sem data";
                                      try {
                                        const d = new Date(val);
                                        if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
                                      } catch (e) {}
                                      return val;
                                    })()}
                                  </div>
                                </div>
                                <div className="text-right shrink-0">
                                  <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded ${
                                    isSelected ? "bg-white text-emerald-600" : "bg-slate-100 text-slate-600 border border-slate-200"
                                  }`}>
                                    EM ESTOQUE
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Lotes em Produção (PE) */}
                    {hasPECandidates && (
                      <div className="border-t border-slate-200/80 pt-3">
                        <h4 className="text-xs font-bold text-orange-800 uppercase flex items-center gap-1.5 mb-2">
                          <Layers size={14} className="text-orange-600" /> Item em Produção de Estoque (PE) na Fábrica
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                          {peCandidates.map((candidate) => {
                            const isSelected = stockItem?.id === candidate.id && formData.useStock;
                            const stageLabel = getStatusLabel(candidate.status);
                            return (
                              <div
                                key={candidate.id}
                                onClick={() => {
                                  setSelectedStockItemId(candidate.id);
                                  setFormData(prev => ({
                                    ...prev,
                                    useStock: true,
                                    initialStatus: "INTERCEPTADO_FABRICA_ESTOQUE",
                                    isStatusLocked: true
                                  }));
                                }}
                                className={`p-2.5 rounded-lg border text-left cursor-pointer transition-all flex items-center justify-between ${
                                  isSelected
                                    ? "bg-orange-500 text-white border-orange-600 shadow-md ring-2 ring-orange-400"
                                    : "bg-white hover:bg-orange-50 text-slate-700 border-slate-200"
                                }`}
                              >
                                <div className="min-w-0 flex-1 pr-2">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${
                                      isSelected ? "bg-white/20 text-white" : "bg-orange-100 text-orange-800"
                                    }`}>
                                      LOTE
                                    </span>
                                    <span className="font-mono text-[11px] font-bold truncate">
                                      ID: {candidate.id.substring(0, 8)}
                                    </span>
                                  </div>
                                  <div className={`text-[10px] font-medium mt-1 truncate ${
                                    isSelected ? "text-orange-100" : "text-slate-500"
                                  }`}>
                                    Criado em: {(() => {
                                      const val = candidate.dateIn || candidate.created;
                                      if (!val) return "Sem data";
                                      try {
                                        const d = new Date(val);
                                        if (!isNaN(d.getTime())) return d.toLocaleDateString("pt-BR");
                                      } catch (e) {}
                                      return val;
                                    })()}
                                  </div>
                                </div>
                                
                                <div className="text-right shrink-0">
                                  <span className={`text-[10px] font-extrabold uppercase px-2 py-0.5 rounded ${
                                    isSelected ? "bg-white text-orange-600" : "bg-slate-100 text-slate-600 border border-slate-200"
                                  }`}>
                                    {stageLabel}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!anyStockAvailable && !isEditing && (
                <div className="flex justify-end mb-2">
                  <StatusSelector />
                </div>
              )}
            </>
          )}

          {/* 1. ESPECIFICAÇÕES TÉCNICAS */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-bold text-yellow-800 flex items-center gap-2 text-sm uppercase">
                <Gem size={16} /> Especificações da Joia
              </h3>
            </div>

            {/* --- CAMPO EXCESSÃO --- */}
            <div className="mb-4">
              <label className="text-[10px] font-bold text-red-600 uppercase flex items-center gap-1 mb-1">
                <Siren size={12} /> Exceção / Aviso Vital (Produção)
              </label>
              <input
                type="text"
                className="w-full p-2 border border-red-300 rounded outline-none focus:border-red-500 font-bold text-red-700 bg-red-50 placeholder:text-red-300 placeholder:font-normal text-xs"
                placeholder="Exemplo: Peça deve ser feita de ponta cabeça..."
                value={formData.exception}
                onChange={(e) => update("root", "exception", e.target.value)}
              />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 border-b border-yellow-200 pb-4">
              <div>
                <label className="lbl">Aro / Tamanho</label>
                <input
                  type="text"
                  className="ipt font-bold text-blue-700"
                  value={formData.specs.size}
                  onChange={(e) => update("specs", "size", e.target.value)}
                />
              </div>
              <div>
                <label className="lbl">Tipo de Pedra</label>
                <select
                  className={`ipt ${isNatural
                    ? "bg-blue-50 text-blue-800 border-blue-300 font-bold"
                    : "bg-white"
                    }`}
                  value={formData.specs.stoneType}
                  onChange={(e) => update("specs", "stoneType", e.target.value)}
                >
                  <option value="">Selecione...</option>
                  <option value="Zirconia">Zircônia</option>
                  <option value="Natural">Natural</option>
                  <option value="CNAT">Centro Natural</option>
                  <option value="ND">Não Aplica</option>
                </select>
                {isNatural && (
                  <div className="flex items-center gap-1 mt-1 text-[9px] font-bold text-blue-600 animate-pulse">
                    <ShieldCheck size={10} /> <span>Natural</span>
                  </div>
                )}
              </div>
              <div>
                <label className="lbl">Lote da Pedra</label>
                <input
                  type="text"
                  className="ipt"
                  placeholder="Ex: LT-2023"
                  value={formData.specs.stoneBatch || ""}
                  onChange={(e) =>
                    update("specs", "stoneBatch", e.target.value)
                  }
                />
              </div>
              <div>
                <label className="lbl">Cor Escolhida</label>
                <select
                  className={`ipt font-bold ${showColorWarning
                    ? "border-amber-400 bg-amber-50 text-amber-900"
                    : "bg-white"
                    }`}
                  value={formData.specs.stoneColor}
                  onChange={(e) =>
                    update("specs", "stoneColor", e.target.value)
                  }
                >
                  <option value="">Selecione...</option>
                  {STONE_COLORS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                  {formData.specs.stoneColor &&
                    !STONE_COLORS.includes(formData.specs.stoneColor) && (
                      <option value={formData.specs.stoneColor}>
                        {formData.specs.stoneColor}
                      </option>
                    )}
                </select>
                {showColorWarning && (
                  <div className="flex items-center gap-1 mt-1 text-[9px] font-bold text-amber-600 animate-pulse">
                    <AlertTriangle size={10} /> <span>Difere do Padrão</span>
                  </div>
                )}
              </div>
              <div>
                <label className="lbl">Finalização</label>
                <input
                  type="text"
                  className="ipt"
                  value={formData.specs.finishing}
                  onChange={(e) => update("specs", "finishing", e.target.value)}
                />
              </div>
              <div className="col-span-1 md:col-span-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <label className="lbl !mb-0">Gravação</label>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        type="button"
                        onClick={() => {
                          const curr = formData.specs.engraving || "";
                          const addition = curr.trim() ? " // Medalha 2: " : "Medalha 1: ";
                          update("specs", "engraving", curr + addition);
                        }}
                        className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                        title="Adicionar atalho de Medalha"
                      >
                        + Medalha
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const curr = formData.specs.engraving || "";
                          const addition = curr.trim() ? " // Verso: " : "Frente: ";
                          update("specs", "engraving", curr + addition);
                        }}
                        className="text-[10px] font-semibold text-purple-700 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-1.5 py-0.5 rounded cursor-pointer transition-colors"
                        title="Adicionar atalho de Verso / Frente"
                      >
                        + Verso
                      </button>
                    </div>
                  </div>

                  {(() => {
                    const count = (formData.specs.engraving || "").length;
                    const style = getEngravingBadgeStyle(count);
                    return (
                      <span
                        className={`text-[11px] px-2.5 py-0.5 rounded-full border flex items-center gap-1 transition-all ${
                          count > 0 ? style.badgeClass : "bg-slate-100 text-slate-400 border-slate-200"
                        }`}
                        title={
                          count > 15
                            ? "Atenção: gravação total com mais de 15 caracteres"
                            : count > 10
                            ? "Alerta: gravação total com mais de 10 caracteres"
                            : "Tamanho ideal"
                        }
                      >
                        {count > 15 && <AlertTriangle size={11} className="text-red-500" />}
                        {count > 10 && count <= 15 && <AlertTriangle size={11} className="text-amber-500" />}
                        <span className="font-extrabold">{count}</span>
                        <span className="font-normal text-[10px]">
                          {count === 1 ? "caractere total" : "caracteres totais"}
                        </span>
                      </span>
                    );
                  })()}
                </div>

                {/* Bloco de Edição Direta / Inteligente (Apenas quando houver mais de uma peça!) */}
                {(() => {
                  const parts = parseEngravingParts(formData.specs.engraving);
                  const hasMultipleParts = parts.length > 1;

                  if (hasMultipleParts) {
                    return (
                      <div className="space-y-2 bg-purple-50/40 p-2.5 rounded-xl border border-purple-100">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-extrabold text-purple-800 uppercase tracking-wider flex items-center gap-1">
                            <PenTool size={11} className="text-purple-600" />
                            Peças Identificadas (Edite o texto diretamente em cada campo):
                          </span>
                          <span className="text-[10px] text-purple-600 font-semibold">
                            {parts.length} peças
                          </span>
                        </div>

                        {/* Campos de entrada direta para cada peça */}
                        <EngravingDisplay
                          rawText={formData.specs.engraving}
                          mode="detailed"
                          editable={true}
                          onUpdatePart={handleUpdateEngravingPart}
                        />

                        {/* Opção para editar a string bruta se o usuário preferir */}
                        <div className="pt-2 border-t border-purple-100/80">
                          <details className="text-xs text-slate-500">
                            <summary className="cursor-pointer hover:text-purple-700 text-[11px] font-semibold select-none">
                              Editar texto bruto unificado (avançado)
                            </summary>
                            <input
                              type="text"
                              className="ipt mt-1.5"
                              value={formData.specs.engraving || ""}
                              onChange={(e) => update("specs", "engraving", e.target.value)}
                              placeholder="Texto bruto da gravação..."
                            />
                          </details>
                        </div>
                      </div>
                    );
                  }

                  // Gravação simples de peça única sem rótulo
                  return (
                    <input
                      type="text"
                      className="ipt"
                      value={formData.specs.engraving || ""}
                      onChange={(e) => update("specs", "engraving", e.target.value)}
                      placeholder="Ex: Amor Eterno // Medalha 2: 25.12"
                    />
                  );
                })()}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="lbl">Cor Padrão</label>
                <input
                  type="text"
                  className="ipt bg-slate-100 text-slate-500"
                  readOnly
                  value={formData.specs.standardColor}
                />
              </div>
              <div>
                <label className="lbl">Tipo de Joia</label>
                <input
                  type="text"
                  className="ipt bg-slate-100 text-slate-500"
                  readOnly
                  value={formData.specs.jewelryType}
                />
              </div>
              <div>
                <label className="lbl">Material</label>
                <input
                  type="text"
                  className="ipt bg-slate-100 text-slate-500"
                  readOnly
                  value={formData.specs.material}
                />
              </div>
              <div>
                <label className="lbl">Categoria</label>
                <input
                  type="text"
                  className="ipt bg-slate-100 text-slate-500"
                  readOnly
                  value={formData.specs.category}
                />
              </div>
            </div>
          </div>

          {/* DADOS EXTRAS */}
          {showFullForm && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2 text-sm uppercase">
                    <User size={16} /> Cliente & Contato
                  </h3>
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-1">
                        <label className="lbl">Nº Pedido</label>
                        <input
                          type="text"
                          className="ipt"
                          value={formData.orderNumber}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              orderNumber: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="lbl">Data Entrada</label>
                        <input
                          type="date"
                          className="ipt"
                          value={formData.customDate}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              customDate: e.target.value,
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="lbl">Nome Completo</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.order.customer.name}
                        onChange={(e) =>
                          update("order", "customer", e.target.value, "name")
                        }
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="lbl">CPF</label>
                        <input
                          type="text"
                          className="ipt"
                          value={formData.order.customer.cpf}
                          onChange={(e) =>
                            update("order", "customer", e.target.value, "cpf")
                          }
                        />
                      </div>
                      <div>
                        <label className="lbl">Zap</label>
                        <input
                          type="text"
                          className="ipt"
                          value={formData.order.customer.phone}
                          onChange={(e) =>
                            update("order", "customer", e.target.value, "phone")
                          }
                        />
                      </div>
                    </div>
                    <div>
                      <label className="lbl">Email</label>
                      <input
                        type="email"
                        className="ipt"
                        value={formData.order.customer.email}
                        onChange={(e) =>
                          update("order", "customer", e.target.value, "email")
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 flex flex-col">
                  <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2 text-sm uppercase">
                    <CreditCard size={16} /> Pagamento & Notas
                  </h3>
                  <div className="space-y-3 flex-1">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="lbl">Valor Total (R$)</label>
                        <input
                          type="text"
                          className="ipt"
                          value={formData.order.payment.total}
                          onChange={(e) =>
                            update("order", "payment", e.target.value, "total")
                          }
                        />
                      </div>
                      <div>
                        <label className="lbl">Forma Pagto.</label>
                        <input
                          type="text"
                          className="ipt"
                          placeholder="Ex: PIX"
                          value={formData.order.payment.method}
                          onChange={(e) =>
                            update("order", "payment", e.target.value, "method")
                          }
                        />
                      </div>
                    </div>

                    <div className="h-full">
                      <label className="lbl">Observações</label>
                      <textarea
                        className="w-full p-2 border rounded-lg text-sm h-24 resize-none focus:border-purple-500 outline-none"
                        value={formData.order.notes}
                        onChange={(e) =>
                          update("order", "notes", e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Logística de Envio */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mt-4">
                <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2 text-sm uppercase">
                  <Truck size={16} /> Logística de Envio
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                  <div>
                    <label className="lbl">Método Envio</label>
                    <input
                      type="text"
                      className="ipt font-bold text-purple-700"
                      value={formData.shipping.tipoenvio}
                      onChange={(e) =>
                        update("shipping", "tipoenvio", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="lbl">Custo Frete</label>
                    <input
                      type="text"
                      className="ipt"
                      value={formData.shipping.price}
                      onChange={(e) =>
                        update("shipping", "price", e.target.value)
                      }
                    />
                  </div>
                  <div>
                    <label className="lbl">Rastreamento</label>
                    <input
                      type="text"
                      className="ipt"
                      value={formData.shipping.tracking}
                      onChange={(e) =>
                        update("shipping", "tracking", e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-2 mb-2 text-xs font-bold text-slate-400 uppercase">
                    <MapPin size={12} /> Endereço de Entrega
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="col-span-2">
                      <label className="lbl">Destinatário</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.destinatario}
                        onChange={(e) =>
                          update(
                            "shipping",
                            "address",
                            e.target.value,
                            "destinatario"
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="lbl">CEP</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.zip}
                        onChange={(e) =>
                          update("shipping", "address", e.target.value, "zip")
                        }
                      />
                    </div>
                    <div>
                      <label className="lbl">UF</label>
                      <input
                        type="text"
                        className="ipt"
                        maxLength={2}
                        value={formData.shipping.address.statecode}
                        onChange={(e) =>
                          update(
                            "shipping",
                            "address",
                            e.target.value,
                            "statecode"
                          )
                        }
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="lbl">Rua</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.street}
                        onChange={(e) =>
                          update(
                            "shipping",
                            "address",
                            e.target.value,
                            "street"
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="lbl">Número</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.number}
                        onChange={(e) =>
                          update(
                            "shipping",
                            "address",
                            e.target.value,
                            "number"
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="lbl">Bairro</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.district}
                        onChange={(e) =>
                          update(
                            "shipping",
                            "address",
                            e.target.value,
                            "district"
                          )
                        }
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="lbl">Complemento</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.complemento}
                        onChange={(e) =>
                          update(
                            "shipping",
                            "address",
                            e.target.value,
                            "complemento"
                          )
                        }
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="lbl">Cidade</label>
                      <input
                        type="text"
                        className="ipt"
                        value={formData.shipping.address.city}
                        onChange={(e) =>
                          update("shipping", "address", e.target.value, "city")
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="p-4 bg-slate-50 border-t flex justify-end gap-3 shrink-0">
          <button
            onClick={onClose}
            disabled={submitting}
            className={`px-5 py-2.5 text-slate-500 font-bold hover:bg-slate-200 rounded-lg text-sm ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-6 py-2.5 text-white font-bold rounded-lg flex items-center gap-2 text-sm shadow-lg ${
              isSaveOnlyMode 
                ? "bg-blue-600 hover:bg-blue-700 shadow-blue-200" 
                : "bg-purple-600 hover:bg-purple-700 shadow-purple-200"
            } ${submitting ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin" />
            ) : isSaveOnlyMode ? (
              <Save size={18} />
            ) : isEditing ? (
              <Edit3 size={18} />
            ) : (
              <Factory size={18} />
            )}{" "}
            {submitting 
              ? "Processando..." 
              : isSaveOnlyMode 
                ? "Salvar Alterações" 
                : isEditing 
                  ? "Salvar Alterações" 
                  : "Confirmar Ordem"}
          </button>
        </div>

        {/* ESTILOS */}
        <style>{`.lbl { display: block; font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; margin-bottom: 4px; } .ipt { width: 100%; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; font-size: 13px; outline: none; transition: border-color 0.2s; } .ipt:focus { border-color: #a855f7; }`}</style>
      </div>
    </div>
  );
}
